/**
 * Reading a weight from the workstation's RS-232 scale through the local hardware service.
 *
 * The successor of the Delphi pawn form's F3 / "Citeste cantar": one stable weight in grams goes
 * into a metal row's quantity. The service finds the scale on the serial ports by itself; this page
 * only asks for a weight and shows why, when there is none. See
 * `documentation/scale_integration_analysis.md`.
 *
 * Every function here returns a value and never throws: a scale that is off, a service that is not
 * installed and a reply this page does not understand all end as a code the operator can be told.
 */
import {
    HARDWARE_SERVICE_ORIGIN,
    type FetchLike,
    type HardwareServiceStatus,
} from './labelPrinting';

/** The capability the service advertises when this workstation can have a scale. */
export const CAPABILITY_SCALE_WEIGHT = 'scale.weight';

/**
 * How long a read may take before the page gives up.
 *
 * Well above the service's own 5-second wait for a stable value, because the first read on a
 * workstation also searches the serial ports for the scale (about 3 seconds per port).
 */
export const SCALE_REQUEST_TIMEOUT_MS = 30_000;

/** The codes the service answers with. */
const SERVICE_CODES = [
    'SCALE_NOT_FOUND',
    'SCALE_BUSY',
    'SCALE_PORT_BUSY',
    'SCALE_UNSTABLE',
    'SCALE_NEGATIVE',
    'SCALE_OVERLOAD',
    'SCALE_WRONG_UNIT',
    'SCALE_ERROR',
    'SCALE_NOT_IMPLEMENTED',
] as const;

/** Why a read produced no weight. */
export type ScaleErrorCode =
    | (typeof SERVICE_CODES)[number]
    /** Nothing answered on the loopback port. */
    | 'SERVICE_UNAVAILABLE'
    /** The call took longer than {@link SCALE_REQUEST_TIMEOUT_MS}. */
    | 'REQUEST_TIMEOUT'
    /** The service answered something this page does not understand. */
    | 'UNEXPECTED_RESPONSE';

/** Every code, for translation completeness checks. */
export const SCALE_ERROR_CODES: readonly ScaleErrorCode[] = [
    ...SERVICE_CODES,
    'SERVICE_UNAVAILABLE',
    'REQUEST_TIMEOUT',
    'UNEXPECTED_RESPONSE',
];

/** One stable weight. */
export interface ScaleReading {
    /** Grams, rounded to 2 decimals (the metal-quantity rule). */
    grams: number;
    /** The port the scale is on, e.g. `COM3`. */
    port: string;
    /** The dialect it answered in. */
    protocol: string;
}

/** The outcome of a read. */
export type ScaleResult = { ok: true; reading: ScaleReading } | { ok: false; code: ScaleErrorCode };

/** What the service remembers about this workstation's scale. */
export interface ScaleStatus {
    detected: boolean;
    port: string | null;
    protocol: string | null;
}

/** Whether the service is ready, paired and offers the scale. */
export function canReadScale(status: HardwareServiceStatus | null): boolean {
    return (
        status?.state === 'ready' && status.health.capabilities.includes(CAPABILITY_SCALE_WEIGHT)
    );
}

/**
 * Whether a key press is the scale shortcut: F2 alone.
 *
 * F2, not the Delphi F3, because F3 is the browser's "find next"; F2 does nothing in Chrome, Edge or
 * Firefox on Windows, nor anywhere else in this application (owner decision, analysis §4).
 */
export function isScaleShortcut(event: {
    key: string;
    altKey: boolean;
    ctrlKey: boolean;
    metaKey: boolean;
    shiftKey: boolean;
}): boolean {
    return (
        event.key === 'F2' && !event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey
    );
}

/** Rounds grams to the 2 decimals a metal quantity carries. */
export function roundGrams(value: number): number {
    return Math.round(value * 100) / 100;
}

/** The translation key for a code. */
export function scaleErrorKey(code: ScaleErrorCode): string {
    return `scale.errors.${code}`;
}

/** Reads one stable weight from the remembered scale, searching for it first if none is known. */
export function readScaleWeight(fetchImpl: FetchLike): Promise<ScaleResult> {
    return weigh(fetchImpl, '/v1/scale/read');
}

/** The "Test read" button: forget what is known, search every port again, then read. */
export function detectScale(fetchImpl: FetchLike): Promise<ScaleResult> {
    return weigh(fetchImpl, '/v1/scale/detect');
}

/** What the service remembers about the scale, or null when it cannot be asked. Touches no port. */
export async function describeScale(fetchImpl: FetchLike): Promise<ScaleStatus | null> {
    try {
        const response = await fetchImpl(`${HARDWARE_SERVICE_ORIGIN}/v1/scale`, { method: 'GET' });

        if (!response.ok) {
            return null;
        }

        const body = (await response.json()) as Partial<ScaleStatus> | null;

        return typeof body?.detected === 'boolean'
            ? { detected: body.detected, port: text(body.port), protocol: text(body.protocol) }
            : null;
    } catch {
        return null;
    }
}

async function weigh(fetchImpl: FetchLike, path: string): Promise<ScaleResult> {
    const controller = new AbortController();
    let expired = false;
    const timeout = setTimeout(() => {
        expired = true;
        controller.abort();
    }, SCALE_REQUEST_TIMEOUT_MS);

    let outcome: ScaleResult;

    try {
        const response = await fetchImpl(`${HARDWARE_SERVICE_ORIGIN}${path}`, {
            method: 'POST',
            signal: controller.signal,
        });

        outcome = response.ok ? await reading(response) : await refusal(response);
    } catch {
        outcome = { ok: false, code: expired ? 'REQUEST_TIMEOUT' : 'SERVICE_UNAVAILABLE' };
    }

    // After the await rather than in a `finally`: reading and refusal never throw, so every way the
    // call can end already arrives here.
    clearTimeout(timeout);

    return outcome;
}

async function reading(response: Response): Promise<ScaleResult> {
    const body = (await parse(response)) as Partial<ScaleReading> | null;
    const grams = body?.grams;
    const port = text(body?.port);
    const protocol = text(body?.protocol);

    if (
        typeof grams !== 'number' ||
        !Number.isFinite(grams) ||
        grams < 0 ||
        port === null ||
        protocol === null
    ) {
        return { ok: false, code: 'UNEXPECTED_RESPONSE' };
    }

    return { ok: true, reading: { grams: roundGrams(grams), port, protocol } };
}

async function refusal(response: Response): Promise<ScaleResult> {
    const body = (await parse(response)) as { error?: unknown } | null;
    const code = body?.error;

    // Only a code someone wrote a sentence for reaches the operator.
    return {
        ok: false,
        code: SERVICE_CODES.find(known => known === code) ?? 'UNEXPECTED_RESPONSE',
    };
}

async function parse(response: Response): Promise<unknown> {
    try {
        return await response.json();
    } catch {
        return null;
    }
}

function text(value: unknown): string | null {
    return typeof value === 'string' && value.length > 0 ? value : null;
}
